import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, Empty, Spin } from 'antd';
import { useLazyQuery } from '@apollo/client';

import { RedirectIcon, CloseIcon } from 'components/Icons';
import { GET_SPAM_PROPOSALS } from 'graphql/queries/proposalQueries';
import utils from 'utils/utils';
import { makeNavigableLink } from 'utils/spamUrl';

const PAGE_SIZE = 20;

const SpamProposals = ({ spamUrl, onClose }) => {
  const [proposals, setProposals] = useState([]);
  const [cursorPaging, setCursorPaging] = useState(null);
  const [isFetching, setIsFetching] = useState(true);

  // -------------- fetch, fetchMore spam proposals ---------------
  const [fetchSpamProposals, { fetchMore }] = useLazyQuery(GET_SPAM_PROPOSALS, {
    fetchPolicy: 'network-only',
    onCompleted: ({ fetchProposals }) => {
      processFetchedData(fetchProposals);
    },
  });

  const processFetchedData = (data) => {
    const { pagingMetadata, proposals } = data;
    const _proposals = proposals.map((l) => ({
      _id: l._id,
      pid: l.pid,
      pnum: l.pnum,
      uid: l.uid,
      email: l?.email,
      name: l?.project.name,
      state: l.state,
      spamUrls: l?.spamUrls,
    }));
    setProposals([..._proposals]);
    setCursorPaging(pagingMetadata);
    setIsFetching(false);
  };

  const buildQueryOptions = () => {
    return {
      variables: {
        spamUrl: spamUrl,
        limit: PAGE_SIZE,
        cursorPaging: cursorPaging?.cursor,
      },
    };
  };

  useEffect(() => {
    // initial load
    setIsFetching(true);
    fetchSpamProposals(buildQueryOptions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = async () => {
    setIsFetching(false);
    const { data } = await fetchMore(buildQueryOptions());
    processFetchedData(data?.fetchProposals || {});
  };
  // -------------- END of fetch, fetchMore spam proposals ---------------

  return (
    <Modal
      className="spam-proposals"
      visible={true}
      footer={
        <div className="button-wrapper">
          <div>
            {cursorPaging?.hasNext && (
              <Button
                loading={isFetching}
                disabled={isFetching}
                className="medium-btn grey-btn"
                onClick={loadMore}>
                Load more
              </Button>
            )}
          </div>

          <Button className="medium-btn grey-btn" onClick={onClose}>
            Close
          </Button>
        </div>
      }
      width={800}
      closeIcon={<CloseIcon className="close-icon" />}
      onCancel={onClose}>
      <div>
        <h3 className="title">Proposals with spam proposals</h3>

        <div className="divider" />
        <div>
          {isFetching && <Spin />}

          {!isFetching && (
            <>
              {proposals.length ? (
                proposals.map((p) => (
                  <div key={p._id} className="proposal-card">
                    <div className="info-row">
                      <div className="name">{p.name}</div>
                      <div className="state">{p.state}</div>
                    </div>
                    <br />
                    <div className="info-row">
                      <div className="ant-col-12">
                        <strong>_id</strong> : {p._id}
                      </div>
                      <div className="ant-col-12">
                        <strong>pid</strong> : {p.pid}
                      </div>
                    </div>
                    <div className="info-row">
                      <div className="ant-col-12">
                        <strong>userId</strong> : {p.uid}
                      </div>
                      <div className="ant-col-12">
                        <strong>Email</strong> : {p.email}
                      </div>
                    </div>
                    <div className="info-row spam-links">
                      {p.spamUrls.map((p) => (
                        <span
                          key={p.url}
                          className={`url ${p.blocked ? 'blocked' : 'whitelisted'}`}>
                          <a href={makeNavigableLink(p.url)} target="_blank" rel="noreferrer">
                            {utils.cutLongText(p.url)}
                            &nbsp;
                            <RedirectIcon />
                          </a>
                          &nbsp;&nbsp;
                          <strong>
                            {'('}
                            {p.blocked ? 'Blocked' : 'Whitelisted'}
                            {')'}
                          </strong>
                        </span>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <Empty />
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

SpamProposals.defaultProps = {
  spamUrl: '',
  onClose: () => {},
};

SpamProposals.propTypes = {
  spamUrl: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SpamProposals;
